
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet, Upload, X, Check, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";
import { ClienteService } from "@/services/StorageService";
import { useAuth } from "@/contexts/AuthContext";

interface ClienteImportData {
  nome: string;
  documento: string;
  email: string;
  celular: string;
  status: string;
  isValid: boolean;
  errors?: string[];
}

const ClienteImporter = () => {
  const [importData, setImportData] = useState<ClienteImportData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { user } = useAuth();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if it's an Excel file
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      toast.error("Por favor, envie um arquivo Excel (.xlsx, .xls) ou CSV");
      return;
    }

    setIsUploading(true);

    try {
      if (file.name.endsWith('.csv')) {
        // Handle CSV
        const text = await file.text();
        const rows = text.split('\n');
        
        // Skip the header row and parse data
        const parsedData = rows.slice(1).filter(row => row.trim() !== '').map((row, index) => {
          const columns = row.split(',');
          
          // Basic validation
          const nome = columns[0]?.trim() || '';
          const documento = columns[1]?.trim() || '';
          const email = columns[2]?.trim() || '';
          const celular = columns[3]?.trim() || '';
          const status = columns[4]?.trim() || 'Ativo';
          
          const errors: string[] = [];
          if (nome.length < 3) errors.push('Nome inválido');
          if (documento.length < 11) errors.push('Documento inválido');
          if (email && !email.includes('@')) errors.push('Email inválido');
          
          return {
            nome,
            documento,
            email,
            celular,
            status,
            isValid: errors.length === 0,
            errors
          };
        });
        
        setImportData(parsedData);
      } else {
        // For Excel files, we'd use a library like SheetJS/xlsx
        // Since we can't add new dependencies directly, we'll simulate it with a toast message
        toast.error("Importação de arquivos Excel requer uma biblioteca adicional. Por favor, use CSV por enquanto.");
      }
    } catch (error) {
      console.error("Erro ao processar arquivo:", error);
      toast.error("Erro ao processar o arquivo. Verifique o formato.");
    } finally {
      setIsUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const importClientes = () => {
    const validClientes = importData.filter(client => client.isValid);
    
    if (validClientes.length === 0) {
      toast.error("Não há clientes válidos para importar");
      return;
    }
    
    try {
      // Add the valid clients to the database
      validClientes.forEach(client => {
        // Create a simplified client object
        const clienteData = {
          geradorId: "", // This would need to be selected by the user in a real scenario
          nome: client.nome,
          documento: client.documento,
          email: client.email || "",
          celular: client.celular || "",
          status: client.status || "Ativo",
          cep: "",
          logradouro: "",
          bairro: "",
          cidade: "",
          estado: "",
          concessionaria: "",
          numeroCliente: "",
          numeroInstalacao: "",
          gastoEnergia: 0,
          tarifaEnergia: 0,
          criadoPor: user?.id
        };
        
        // In a real implementation, we would save this to the database
        // ClienteService.create(clienteData);
      });
      
      toast.success(`${validClientes.length} clientes importados com sucesso!`);
      setImportData([]);
    } catch (error) {
      console.error("Erro ao importar clientes:", error);
      toast.error("Erro ao importar clientes");
    }
  };

  const downloadTemplate = () => {
    const csvContent = "Nome,Documento,Email,Celular,Status\n";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_clientes.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-energy-blue-dark">Importar Clientes</CardTitle>
        <CardDescription>
          Importe uma lista de clientes a partir de um arquivo CSV.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border border-dashed rounded-md">
          <div className="flex items-center">
            <FileSpreadsheet className="h-10 w-10 text-muted-foreground mr-4" />
            <div>
              <h3 className="font-medium">Upload de Arquivo</h3>
              <p className="text-sm text-muted-foreground">
                Faça upload de um arquivo CSV com os dados dos clientes
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="whitespace-nowrap"
            >
              <Download className="h-4 w-4 mr-2" />
              Template
            </Button>
            <Button
              asChild
              className="bg-energy-blue text-white hover:bg-energy-blue-dark"
            >
              <label className="cursor-pointer flex items-center">
                <Upload className="h-4 w-4 mr-2" />
                Selecionar CSV
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                />
              </label>
            </Button>
          </div>
        </div>

        {importData.length > 0 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-lg">
                Dados para Importação ({importData.length} registros)
              </h3>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setImportData([])}
                  className="text-red-500 border-red-500 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  onClick={importClientes}
                  className="bg-energy-green text-white hover:bg-energy-green-dark"
                  disabled={!importData.some(client => client.isValid)}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Importar Válidos
                </Button>
              </div>
            </div>

            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">Status</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Celular</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importData.map((client, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {client.isValid ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        )}
                      </TableCell>
                      <TableCell className={!client.isValid ? "text-red-500" : ""}>
                        {client.nome}
                      </TableCell>
                      <TableCell className={!client.isValid ? "text-red-500" : ""}>
                        {client.documento}
                      </TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>{client.celular}</TableCell>
                      <TableCell>{client.status || "Ativo"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {importData.some(client => !client.isValid) && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                <h4 className="font-medium flex items-center text-yellow-800">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Alguns registros possuem problemas
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Corrija os erros no arquivo de origem e faça o upload novamente, ou importe apenas os registros válidos.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClienteImporter;
